import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, Maximize2, Palette, Loader2 } from 'lucide-react';
import './ComicBlock.css';
import { ICON_SIZE } from '../design-system';

// Module-level cache: tracks which URLs have successfully loaded or errored this session.
// Survives component remounts so the loading state is never shown twice for the same URL.
const loadedUrls = new Set();
const failedUrls = new Set();

/**
 * ComicBlock - Inline comic artifact card for AI chat responses
 *
 * Rendered by ReedleMarkdown when it encounters a ```reedle:comic block.
 * Displays generated comic image with model info, credit cost, and actions.
 *
 * @param {string} url - Public URL of the generated comic image
 * @param {string} model - Model display name (e.g., "Nano Banana")
 * @param {number} credits - Credits used for generation
 * @param {function} onOpenLightbox - Callback to open fullscreen lightbox with the image
 */
const ComicBlock = ({ url, model, style, credits, onOpenLightbox }) => {
  const { t } = useTranslation('reader');
  // Lazy initialisers read from cache so a remounted block skips the loading state
  // for URLs that have already been successfully loaded or confirmed as errored.
  const [imageLoaded, setImageLoaded] = useState(() => loadedUrls.has(url));
  const [imageError, setImageError] = useState(() => failedUrls.has(url));

  const handleImageClick = () => {
    if (url) {
      if (onOpenLightbox) {
        onOpenLightbox(url);
      } else {
        window.open(url, '_blank', 'noopener,noreferrer');
      }
    }
  };

  const handleDownload = async (e) => {
    e?.stopPropagation();
    if (!url) return;

    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `comic-${Date.now()}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } catch {
      // Fallback: open in new tab
      window.open(url, '_blank');
    }
  };

  if (!url) return null;

  return (
    <div className="comic-block">
      {/* Image preview */}
      <div
        className={`comic-block-image-wrapper ${imageLoaded ? 'loaded' : ''}`}
        onClick={handleImageClick}
        role="button"
        tabIndex={0}
        title={t('comic.clickFullscreen')}
      >
        {!imageLoaded && !imageError && (
          <div className="comic-block-loading">
            <Loader2 size={ICON_SIZE.lg} className="comic-block-spinner" />
            <span>{t('comic.loading')}</span>
          </div>
        )}
        {imageError ? (
          <div className="comic-block-error">
            <Palette size={ICON_SIZE.lg} />
            <span>{t('comic.failedLoad')}</span>
          </div>
        ) : (
          <img
            src={url}
            alt={t('comic.altText')}
            className="comic-block-image"
            onLoad={() => { loadedUrls.add(url); setImageLoaded(true); }}
            onError={() => { failedUrls.add(url); setImageError(true); }}
          />
        )}
      </div>

      {/* Info bar */}
      <div className="comic-block-info">
        <div className="comic-block-meta">
          <Palette size={ICON_SIZE.xxs} />
          <span>{model || t('comic.aiComic')}</span>
          {style && (
            <span className="comic-block-style">{style}</span>
          )}
          {credits > 0 && (
            <span className="comic-block-credits">{t('comic.credits', { count: credits })}</span>
          )}
        </div>
        <div className="comic-block-actions">
          <button
            className="comic-block-action-btn"
            onClick={handleImageClick}
            title={t('comic.viewFullscreen')}
          >
            <Maximize2 size={ICON_SIZE.xs} />
          </button>
          <button
            className="comic-block-action-btn"
            onClick={handleDownload}
            title={t('comic.download')}
          >
            <Download size={ICON_SIZE.xs} />
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * Parse reedle:comic code block content into structured data.
 * Format:
 *   url: https://...
 *   model: Nano Banana
 *   credits: 127
 */
// eslint-disable-next-line react-refresh/only-export-components -- factory/helper exported alongside its only consumer component
export function parseComicContent(content) {
  const lines = content.trim().split('\n');
  const data = {};
  for (const line of lines) {
    const colonIdx = line.indexOf(':');
    if (colonIdx > 0) {
      const key = line.slice(0, colonIdx).trim();
      const value = line.slice(colonIdx + 1).trim();
      data[key] = value;
    }
  }
  return {
    url: data.url || '',
    model: data.model || '',
    style: data.style || '',
    credits: parseInt(data.credits, 10) || 0,
  };
}

export default ComicBlock;
